import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase, Star, Users, Shield, ChevronRight, AlertTriangle,
  CheckCircle, Clock, CreditCard, Zap, ArrowUpRight, X,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, jobService, documentsService, favouriteJobsService, disputeService, weatherService } from '../services/api';
import { useJobHub } from '../hooks/useSignalR';
import { JobStatus, DocumentType } from '../types';
import type { JobSummaryDto, DocumentUpload, AvailableJobsResponse, HourlyWeather } from '../types';
import type { ReferralDashboardDto, ReferralSummaryDto, DisputeDto, DisputeSummaryDto } from '../types/modules';
import { DisputeStatus } from '../types/modules';
import { RefreshButton } from '../components/RefreshButton';
import { useRefresh } from '../hooks/useRefresh';
import { useDashboardLocation } from '../hooks/useDashboardLocation';
import { useTrafficNews } from '../hooks/useTrafficNews';
import { useInViewOnce } from '../hooks/useInViewOnce';
import { TrafficNewsSection } from '../components/dashboard/TrafficNewsSection';
import { WeatherSection } from '../components/dashboard/WeatherSection';
import { visibleNavCards } from '../lib/navCards';

// ── Types ──────────────────────────────────────────────────────────
interface DashboardSummary {
  fullName: string; role: string; registrationStatus: string;
  availableBalance: number; referralEarnings: number; cashbackCredit: number;
  activeJobs: number; completedJobs: number; pendingQuotes: number;
  averageRating: number; totalReviews: number; profileCompleteness: number;
  pendingActions: string[];
}

type CardKey = 'jobs' | 'payments' | 'referrals' | 'documents' | 'disputes';

// ── Helpers ────────────────────────────────────────────────────────
function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
}

function expiryBand(days: number) {
  if (days < 0)  return { ring: 'border-red-300 bg-red-50',    text: 'text-red-700',    icon: <AlertTriangle className="w-4 h-4 shrink-0 text-red-500" /> };
  if (days < 30) return { ring: 'border-red-200 bg-red-50',    text: 'text-red-600',    icon: <AlertTriangle className="w-4 h-4 shrink-0 text-red-400" /> };
  if (days < 90) return { ring: 'border-amber-200 bg-amber-50', text: 'text-amber-700', icon: <Clock className="w-4 h-4 shrink-0 text-amber-500" /> };
  return           { ring: 'border-green-200 bg-green-50',  text: 'text-green-700',  icon: <CheckCircle className="w-4 h-4 shrink-0 text-green-500" /> };
}

function docLabel(type: DocumentType): string {
  const map: Partial<Record<DocumentType, string>> = {
    [DocumentType.HireRewardInsurance]:      'H&R Insurance',
    [DocumentType.GoodsInTransitInsurance]:  'GIT Insurance',
    [DocumentType.PublicLiabilityInsurance]: 'Public Liability',
    [DocumentType.MotCertificate]:           'MOT Certificate',
    [DocumentType.DrivingLicence]:           'Driving Licence',
  };
  return map[type] ?? type;
}

const ACTIVE_TAB  = new Set([JobStatus.Active, JobStatus.InProgress, JobStatus.CarrierAccepted, JobStatus.ContractGenerated]);
const BIDDING_TAB = new Set([JobStatus.Quoted, JobStatus.Accepted]);
const DONE_TAB    = new Set([JobStatus.Completed, JobStatus.PodVerified, JobStatus.PaymentReleased, JobStatus.ReviewUnlocked]);

const STATUS_CHIP: Partial<Record<JobStatus, { label: string; cls: string }>> = {
  [JobStatus.Draft]:             { label: 'Draft',           cls: 'bg-amber-100 text-amber-700' },
  [JobStatus.Active]:            { label: 'Active',          cls: 'bg-blue-100 text-blue-700' },
  [JobStatus.InProgress]:        { label: 'In Progress',     cls: 'bg-blue-100 text-blue-700' },
  [JobStatus.CarrierAccepted]:   { label: 'Carrier Accepted',cls: 'bg-blue-100 text-blue-700' },
  [JobStatus.ContractGenerated]: { label: 'Contract Ready',  cls: 'bg-indigo-100 text-indigo-700' },
  [JobStatus.Quoted]:            { label: 'Bidding',         cls: 'bg-amber-100 text-amber-700' },
  [JobStatus.Accepted]:          { label: 'Accepted',        cls: 'bg-amber-100 text-amber-700' },
  [JobStatus.Completed]:         { label: 'Delivered',       cls: 'bg-teal-100 text-teal-700' },
  [JobStatus.PodVerified]:       { label: 'POD Verified',    cls: 'bg-teal-100 text-teal-700' },
  [JobStatus.PaymentReleased]:   { label: 'Paid',            cls: 'bg-green-100 text-green-700' },
  [JobStatus.ReviewUnlocked]:    { label: 'Review Due',      cls: 'bg-purple-100 text-purple-700' },
  [JobStatus.Cancelled]:         { label: 'Cancelled',       cls: 'bg-red-100 text-red-700' },
  [JobStatus.Rejected]:          { label: 'Rejected',        cls: 'bg-red-100 text-red-700' },
};

// ── Shared panel wrapper ───────────────────────────────────────────
function PanelWrap({ title, headerCls, onClose, children }: {
  title: string; headerCls: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className={`${headerCls} px-5 py-3.5 flex items-center justify-between`}>
        <h2 className="text-white font-bold text-sm tracking-wide">{title}</h2>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors p-0.5 rounded-lg hover:bg-white/20">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="max-h-[480px] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

// ── Stat card (gradient, clickable) ───────────────────────────────
function StatCard({ icon, label, value, sub, gradient, selected, onClick }: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string;
  gradient: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative w-full text-left ${gradient} text-white rounded-2xl p-4 h-full flex flex-col gap-1 transition-all active:scale-[0.98] ${
        selected ? 'ring-4 ring-white/60 ring-offset-2 scale-[1.02]' : 'hover:scale-[1.01]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="bg-white/20 rounded-xl p-2">{icon}</div>
        {selected
          ? <span className="text-[10px] font-bold bg-white/30 px-2 py-0.5 rounded-full">▼ open</span>
          : <ArrowUpRight className="w-4 h-4 opacity-40" />
        }
      </div>
      <div className="text-xl font-bold mt-2 leading-tight">{value}</div>
      <div className="text-sm opacity-80">{label}</div>
      {sub && <div className="text-xs opacity-55">{sub}</div>}
    </button>
  );
}

const ONGOING_STATUSES = new Set([
  JobStatus.PendingReview,
  JobStatus.Active, JobStatus.Quoted, JobStatus.Accepted,
  JobStatus.ContractGenerated, JobStatus.CarrierAccepted, JobStatus.InProgress,
  JobStatus.Completed, JobStatus.PodVerified,
]);

// ── Jobs panel ────────────────────────────────────────────────────
function JobsPanel({ jobs, onClose }: { jobs: JobSummaryDto[]; onClose: () => void }) {
  const [tab, setTab] = useState<'active' | 'bidding' | 'delivered' | 'pending'>('active');

  const ongoing = jobs.filter(j => ONGOING_STATUSES.has(j.status));

  const sets = {
    active:    ongoing.filter(j => ACTIVE_TAB.has(j.status)),
    bidding:   ongoing.filter(j => BIDDING_TAB.has(j.status)),
    delivered: ongoing.filter(j => j.status === JobStatus.Completed || j.status === JobStatus.PodVerified),
    pending:   ongoing.filter(j => j.status === JobStatus.PendingReview),
  };

  const TABS = [
    { key: 'active'    as const, label: 'Active'    },
    { key: 'bidding'   as const, label: 'Bidding'   },
    { key: 'delivered' as const, label: 'Delivered' },
    { key: 'pending'   as const, label: 'Pending Review' },
  ];

  const current = sets[tab];

  return (
    <PanelWrap title={`Ongoing Jobs (${ongoing.length})`} headerCls="bg-gradient-to-r from-emerald-600 to-green-700" onClose={onClose}>
      {/* Tabs */}
      <div className="flex border-b border-gray-100 overflow-x-auto sticky top-0 bg-white z-10">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${
              tab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {sets[t.key].length > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                tab === t.key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>{sets[t.key].length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Job list */}
      <div className="divide-y divide-gray-50 px-4">
        {current.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No jobs in this category</div>
        ) : (
          current.map(job => {
            const chip = STATUS_CHIP[job.status];
            return (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="flex items-center gap-3 py-3 hover:bg-gray-50 rounded-xl -mx-2 px-2 group transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{job.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {job.referenceNumber} · {job.pickupAddress?.split(',')[0]} → {job.deliveryAddress?.split(',')[0]}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-400 hidden sm:block">
                    {job.scheduledAt ? new Date(job.scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                  </span>
                  {chip && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${chip.cls}`}>
                      {chip.label}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </Link>
            );
          })
        )}
      </div>
      <div className="px-4 py-2.5 border-t border-gray-50">
        <Link to="/dashboards/jobs" className="text-xs text-emerald-600 font-semibold hover:underline">
          View full Jobs Board (all statuses) →
        </Link>
      </div>
    </PanelWrap>
  );
}

// ── Payments panel ─────────────────────────────────────────────────
function PaymentsPanel({ summary, onClose }: { summary: DashboardSummary; onClose: () => void }) {
  const total = (summary.availableBalance ?? 0)
    + (summary.referralEarnings ?? 0)
    + (summary.cashbackCredit ?? 0);

  const rows = [
    { icon: '✅', label: 'Available Funds',   amount: summary.availableBalance ?? 0, color: 'text-green-700',  bg: 'bg-green-50',  hint: 'Ready to withdraw' },
    { icon: '👥', label: 'Referral Earnings', amount: summary.referralEarnings ?? 0, color: 'text-purple-700', bg: 'bg-purple-50', hint: 'Earned from referrals' },
    { icon: '🎁', label: 'Cashback Credit',   amount: summary.cashbackCredit ?? 0,   color: 'text-teal-700',   bg: 'bg-teal-50',   hint: 'Applied on next job' },
  ];

  return (
    <PanelWrap title="Payments" headerCls="bg-gradient-to-r from-purple-600 to-violet-700" onClose={onClose}>
      {/* Total hero */}
      <div className="mx-4 mt-4 mb-3 bg-gradient-to-br from-purple-600 to-violet-700 rounded-2xl px-5 py-4">
        <p className="text-emerald-100 text-xs font-medium mb-1">Total Balance</p>
        <p className="text-white text-3xl font-bold">£{total.toFixed(2)}</p>
        <p className="text-emerald-200 text-xs mt-1">Across all balance types</p>
      </div>

      {/* Balance rows */}
      <div className="px-4 pb-4 space-y-2">
        {rows.map(row => (
          <div key={row.label} className={`${row.bg} rounded-xl px-4 py-3.5 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <span className="text-xl">{row.icon}</span>
              <div>
                <p className={`text-sm font-semibold ${row.color}`}>{row.label}</p>
                <p className="text-xs text-gray-500">{row.hint}</p>
              </div>
            </div>
            <p className={`text-lg font-bold ${row.color}`}>£{row.amount.toFixed(2)}</p>
          </div>
        ))}

        {summary.pendingQuotes > 0 && (
          <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700 font-medium">{summary.pendingQuotes} pending quote{summary.pendingQuotes !== 1 ? 's' : ''} on your jobs</p>
          </div>
        )}
      </div>
    </PanelWrap>
  );
}

// ── Referrals panel ────────────────────────────────────────────────
function ReferralsPanel({ referrals, onClose }: { referrals: ReferralDashboardDto; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const STATUS_STYLE: Record<number, { bg: string; text: string; label: string }> = {
    1: { bg: 'bg-green-100', text: 'text-green-700',  label: 'Active'    },
    2: { bg: 'bg-gray-100',  text: 'text-gray-500',   label: 'Expired'   },
    3: { bg: 'bg-amber-100', text: 'text-amber-700',  label: 'Exhausted' },
    4: { bg: 'bg-red-100',   text: 'text-red-700',    label: 'Suspended' },
  };

  return (
    <PanelWrap title="Referrals" headerCls="bg-gradient-to-r from-orange-500 to-amber-500" onClose={onClose}>
      {/* Code banner */}
      {referrals.yourReferralCode && referrals.yourReferralCode !== 'N/A' && (
        <div className="mx-4 mt-4 mb-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-orange-100 text-xs font-medium mb-1">Your Referral Code</p>
            <p className="text-white text-2xl font-bold tracking-widest font-mono">{referrals.yourReferralCode}</p>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(referrals.yourReferralCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="shrink-0 bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      )}

      {/* Summary stats */}
      <div className="mx-4 mb-3 grid grid-cols-3 gap-2">
        {[
          { label: 'Total',    value: referrals.totalReferrals,               cls: 'bg-gray-50',    val: 'text-gray-900' },
          { label: 'Active',   value: referrals.activeReferrals,              cls: 'bg-green-50',   val: 'text-green-700' },
          { label: 'Earned',   value: `£${(referrals.totalEarned ?? 0).toFixed(2)}`, cls: 'bg-purple-50', val: 'text-purple-700' },
        ].map(s => (
          <div key={s.label} className={`${s.cls} rounded-xl py-3 text-center`}>
            <p className={`text-lg font-bold ${s.val}`}>{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Active referrals only */}
      {(() => {
        const activeRefs = (referrals.referrals as ReferralSummaryDto[]).filter(r => (r.status as number) === 1);
        return activeRefs.length === 0 ? (
          <div className="px-4 pb-8 pt-2 text-center text-gray-400 text-sm">
            No active referrals. Share your code to start earning.
          </div>
        ) : (
          <div className="px-4 pb-4">
            <p className="text-xs text-orange-600 font-semibold mb-2 px-1">Active referrals</p>
            <div className="flex gap-2 px-2 pb-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
              <span className="flex-1">Referee</span>
              <span className="w-12 text-center">Jobs</span>
              <span className="w-16 text-right">Earned</span>
            </div>
            <div className="divide-y divide-gray-50">
              {activeRefs.map((ref, i) => (
                <div key={ref.id} className="flex items-center gap-2 py-2.5">
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-bold w-4 shrink-0">{i + 1}</span>
                    <span className="text-sm font-medium text-gray-800 truncate">{ref.referredUserName}</span>
                  </div>
                  <span className="w-12 text-center text-sm font-bold text-gray-700">{ref.totalJobsReferred}</span>
                  <span className="w-16 text-right text-sm font-bold text-orange-700">£{ref.totalEarned.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="px-4 py-2.5 border-t border-gray-50">
        <Link to="/dashboards/referrals" className="text-xs text-orange-600 font-semibold hover:underline">
          View full Referrals Board →
        </Link>
      </div>
    </PanelWrap>
  );
}

// ── Documents panel ────────────────────────────────────────────────
function DocumentsPanel({ documents, onClose }: { documents: DocumentUpload[]; onClose: () => void }) {
  const allWithExpiry = documents
    .filter(d => d.reviewStatus === 'Approved' && d.expiryDate)
    .map(d => ({ ...d, days: daysUntil(d.expiryDate!) }))
    .sort((a, b) => a.days - b.days);

  // Dashboard only shows at-risk: expired or expiring within 90 days
  const atRisk   = allWithExpiry.filter(d => d.days < 90);
  const pending  = documents.filter(d => d.reviewStatus === 'Pending');
  const rejected = documents.filter(d => d.reviewStatus === 'Rejected');
  const allClear = atRisk.length === 0 && rejected.length === 0 && pending.length === 0;

  return (
    <PanelWrap title="Documents & Compliance" headerCls="bg-gradient-to-r from-gray-600 to-gray-700" onClose={onClose}>
      <div className="px-4 py-4 space-y-2">
        {rejected.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-xs text-red-700 font-medium">{rejected.length} document{rejected.length > 1 ? 's' : ''} rejected — re-upload required</p>
          </div>
        )}
        {pending.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <Clock className="w-4 h-4 text-blue-500 shrink-0" />
            <p className="text-xs text-blue-700 font-medium">{pending.length} document{pending.length > 1 ? 's' : ''} awaiting review</p>
          </div>
        )}

        {allClear ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
            <p className="text-gray-700 font-semibold text-sm">All documents are valid</p>
            <p className="text-gray-400 text-xs mt-1">No documents expiring within the next 90 days</p>
          </div>
        ) : (
          <>
            {atRisk.length > 0 && <p className="text-xs text-gray-500 font-semibold px-1">Expiring or recently expired</p>}
            {atRisk.map(doc => {
              const { ring, text, icon } = expiryBand(doc.days);
              return (
                <Link key={doc.documentId} to="/profile">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${ring} hover:opacity-80 transition-opacity`}>
                    {icon}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${text} truncate`}>{docLabel(doc.documentType)}</p>
                      <p className={`text-xs ${text}`}>
                        {doc.days < 0
                          ? `Expired ${Math.abs(doc.days)} days ago`
                          : doc.days === 0 ? 'Expires today!'
                          : `${doc.days} day${doc.days === 1 ? '' : 's'} remaining`}
                      </p>
                    </div>
                    <p className={`text-xs font-medium ${text} shrink-0`}>
                      {new Date(doc.expiryDate!).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </p>
                  </div>
                </Link>
              );
            })}
          </>
        )}
      </div>
      <div className="px-4 py-2.5 border-t border-gray-50">
        <Link to="/profile" className="text-xs text-gray-600 font-semibold hover:underline">
          Manage all documents →
        </Link>
      </div>
    </PanelWrap>
  );
}

// ── Disputes panel ────────────────────────────────────────────────
const DISPUTE_STATUS_LABEL: Record<number, string> = {
  [DisputeStatus.Open]: 'Open', [DisputeStatus.InReview]: 'In Review',
  [DisputeStatus.Proposed]: 'Resolution Proposed', [DisputeStatus.Resolved]: 'Resolved',
  [DisputeStatus.Escalated]: 'Escalated', [DisputeStatus.Closed]: 'Closed', [DisputeStatus.Withdrawn]: 'Withdrawn',
};

function DisputesPanel({ disputes, onClose }: { disputes: DisputeDto[]; onClose: () => void }) {
  const open = disputes.filter(d => [DisputeStatus.Open, DisputeStatus.InReview, DisputeStatus.Proposed].includes(d.status));

  return (
    <PanelWrap title="Disputes" headerCls="bg-gradient-to-r from-rose-300 to-pink-300" onClose={onClose}>
      <div className="px-4 py-4 space-y-2">
        {open.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
            <p className="text-gray-700 font-semibold text-sm">No open disputes</p>
          </div>
        ) : (
          open.slice(0, 5).map(d => (
            <Link key={d.id} to="/my-disputes" className="flex items-center gap-3 px-4 py-3 rounded-xl border border-rose-100 bg-rose-50 hover:opacity-80 transition-opacity">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-rose-800 truncate">{d.title}</p>
                <p className="text-xs text-rose-600">{DISPUTE_STATUS_LABEL[d.status]} · Ref {d.referenceNumber}</p>
              </div>
            </Link>
          ))
        )}
      </div>
      <div className="px-4 py-2.5 border-t border-gray-50">
        <Link to="/my-disputes" className="text-xs text-gray-600 font-semibold hover:underline">
          Manage all disputes →
        </Link>
      </div>
    </PanelWrap>
  );
}

// Shown in place of Traffic News/Weather until the section is scrolled into view — keeps "Local
// Info" discoverable immediately (so new users know the feature exists) without paying for the
// map/geocoding/external-data fetches it triggers until someone actually scrolls to it.
function LocalInfoSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="dashboard-local-info-skeleton">
      {['Traffic News', 'Local Weather'].map(label => (
        <div key={label} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden animate-pulse">
          <div className="px-5 py-3.5 border-b border-gray-100">
            <span className="font-bold text-sm text-gray-400">{label}</span>
          </div>
          <div className="p-4 h-24 bg-gray-50" />
        </div>
      ))}
    </div>
  );
}

function LiveIndicator({ connected }: { connected: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      connected ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
      {connected ? 'Live updates on' : 'Connecting…'}
    </span>
  );
}

// ── Quick Links — role-gated grid of "my account" pages, shared with the NavBar's
// profile dropdown (App.tsx / lib/navCards.tsx) so both stay in sync ────────────
function QuickLinksGrid({ isCarrier, isWorkshop }: { isCarrier: boolean; isWorkshop: boolean }) {
  return (
    <div data-testid="dashboard-quicklinks-section">
      <h2 className="text-sm font-bold text-gray-700 mb-2">Quick Links</h2>
      <div data-testid="dashboard-quicklinks-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {visibleNavCards({ isCarrier, isWorkshop }).map(card => (
          <Link
            key={card.to}
            to={card.to}
            data-testid={`dashboard-quicklink-${card.to.replace(/\//g, '')}-link`}
            className={`${card.bg} border border-gray-200 rounded-2xl p-3.5 flex items-center gap-2.5 hover:opacity-80 transition-opacity no-underline`}
          >
            <card.icon className="w-5 h-5 shrink-0" style={{ color: card.color }} />
            <span className="text-sm font-semibold text-gray-900 truncate">{card.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────
export default function UserDashboardPage() {
  const { getToken, account } = useAppAuth();
  const [selectedCard, setSelectedCard] = useState<CardKey | null>(null);
  const [liveConnected, setLive]        = useState(false);
  const [liveToast, setLiveToast]       = useState<string | null>(null);
  // Local Info (Traffic News + Weather) is the heaviest part of this page — a native-style map,
  // a geocoding round trip per Job Alert area, and two external-data-backed fetches — so it's
  // lazy-loaded: nothing under it fires until the section is actually scrolled into view.
  const { ref: localInfoRef, inView: localInfoInView } = useInViewOnce<HTMLDivElement>();
  const dashboardLocation = useDashboardLocation(localInfoInView);

  // ── Queries ──────────────────────────────────────────────────────
  const { data: summary, refetch: rSum } = useQuery<DashboardSummary>({
    queryKey: ['dashboard-summary'],
    queryFn:  () => createApiClient(getToken).get<DashboardSummary>('/api/v1/users/dashboard').then(r => r.data),
    enabled:  !!account,
  });

  const { data: myJobs = [], refetch: rJobs } = useQuery<JobSummaryDto[]>({
    queryKey: ['my-jobs-dash'],
    queryFn:  () => jobService(createApiClient(getToken)).getMyJobs(),
    enabled:  !!account,
  });

  const { data: documents = [], refetch: rDocs } = useQuery<DocumentUpload[]>({
    queryKey: ['my-documents-dash'],
    queryFn:  () => documentsService(createApiClient(getToken)).getMy(),
    enabled:  !!account,
  });

  const { data: referrals, refetch: rRef } = useQuery<ReferralDashboardDto>({
    queryKey: ['referral-dash'],
    queryFn:  () => createApiClient(getToken).get<ReferralDashboardDto>('/api/referrals/dashboard').then(r => r.data),
    enabled:  !!account,
  });

  const { data: myDisputes = [], refetch: rDisputes } = useQuery<DisputeDto[]>({
    queryKey: ['my-disputes-dash'],
    queryFn:  () => disputeService(createApiClient(getToken)).getMine(),
    enabled:  !!account,
  });

  const { data: disputeSummary } = useQuery<DisputeSummaryDto>({
    queryKey: ['my-disputes-summary-dash'],
    queryFn:  () => disputeService(createApiClient(getToken)).getSummary(),
    enabled:  !!account,
  });

  const isCarrier = summary?.role === 'Carrier';
  const isWorkshop = summary?.role === 'Workshop';
  const isCarrierOrWorkshop = isCarrier || isWorkshop;

  const { data: availableJobs, refetch: rAvail } = useQuery<AvailableJobsResponse>({
    queryKey: ['available-jobs-dash'],
    queryFn:  () => favouriteJobsService(createApiClient(getToken)).getAvailable(),
    enabled:  !!account && isCarrierOrWorkshop,
  });

  const { data: trafficIncidents = [], isLoading: trafficLoading, error: trafficError } = useTrafficNews(dashboardLocation.trafficLocations, localInfoInView);

  const { data: hourlyWeather = [], isLoading: weatherLoading, error: weatherError } = useQuery<HourlyWeather[]>({
    queryKey: ['weather-hourly-dash', dashboardLocation.weatherCoords?.lat.toFixed(2), dashboardLocation.weatherCoords?.lng.toFixed(2)],
    queryFn:  () => weatherService(createApiClient(getToken)).getHourly(dashboardLocation.weatherCoords!.lat, dashboardLocation.weatherCoords!.lng),
    enabled:  !!account && !!dashboardLocation.weatherCoords && localInfoInView,
    staleTime: 15 * 60_000,
  });

  // ── Refresh ───────────────────────────────────────────────────────
  const refetchAll = useCallback(() => { rSum(); rJobs(); rDocs(); rRef(); rDisputes(); if (isCarrierOrWorkshop) rAvail(); }, [rSum, rJobs, rDocs, rRef, rDisputes, isCarrierOrWorkshop, rAvail]);
  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetchAll);

  // ── SignalR ───────────────────────────────────────────────────────
  const toast = (msg: string) => { setLiveToast(msg); setTimeout(() => setLiveToast(null), 5000); };

  useJobHub({
    getToken, enabled: !!account,
    onJobApproved:       (e) => { toast(`✅ Job approved: ${e.referenceNumber}`); refetchAll(); },
    onPaymentReleased:   (e) => { toast(`💰 Payment released: £${e.netAmount.toFixed(2)}`); refetchAll(); },
    onQuoteReceived:     ()  => { toast('💬 New quote received on your job!'); refetchAll(); },
    onContractGenerated: ()  => { toast('📄 Contract generated — ready to proceed.'); refetchAll(); },
    onNewJobAvailable:   ()  => { refetchAll(); },
    onJobAlertMatch:     ()  => { toast('🔔 A new job matches your alert!'); },
  });

  useEffect(() => { setLive(true); }, []);

  // ── Derived ───────────────────────────────────────────────────────
  const firstName = summary?.fullName?.split(' ')[0] ?? 'there';

  const expiringDocs  = documents
    .filter(d => d.reviewStatus === 'Approved' && d.expiryDate)
    .map(d => ({ ...d, days: daysUntil(d.expiryDate!) }))
    .sort((a, b) => a.days - b.days);
  const approvedDocs  = documents.filter(d => d.reviewStatus === 'Approved').length;
  const expiredCount  = expiringDocs.filter(d => d.days < 0).length;
  const expiringSoon  = expiringDocs.filter(d => d.days >= 0 && d.days < 30).length;
  const docSub        = expiredCount > 0
    ? `${expiredCount} expired!`
    : expiringSoon > 0
    ? `${expiringSoon} expiring soon`
    : approvedDocs > 0 ? 'All valid' : 'No documents';

  function toggleCard(key: CardKey) {
    setSelectedCard(prev => prev === key ? null : key);
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="py-6 space-y-5 w-full">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {firstName} 👋</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {summary?.role && (
              <span className="text-xs font-semibold px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">{summary.role}</span>
            )}
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
              summary?.registrationStatus === 'Verified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {summary?.registrationStatus ?? '…'}
            </span>
            <LiveIndicator connected={liveConnected} />
          </div>
        </div>
        <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
      </div>

      {/* Live toast */}
      {liveToast && (
        <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium shadow-lg">
          <Zap className="w-4 h-4 shrink-0" />{liveToast}
        </div>
      )}

      {/* Pending actions */}
      {(summary?.pendingActions?.length ?? 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 space-y-1.5">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Action Required
          </p>
          {summary!.pendingActions.map((a, i) => (
            <p key={i} className="text-sm text-amber-700 pl-6">• {a}</p>
          ))}
        </div>
      )}

      {/* Profile completeness */}
      {summary && summary.profileCompleteness < 100 && (
        <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Profile Completeness</span>
            <span className="text-sm font-bold text-blue-600">{summary.profileCompleteness}%</span>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-700" style={{ width: `${summary.profileCompleteness}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Complete your profile to improve your bid acceptance rate.{' '}
            <Link to="/profile" className="text-blue-600 hover:underline font-medium">Finish now →</Link>
          </p>
        </div>
      )}

      {/* Stat cards — Available Jobs (carriers/workshops only) sits alongside Active Jobs,
          Payments, Referrals, Documents and Disputes as an equal-size tile in the same row,
          rather than a separate oversized banner. */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${isCarrierOrWorkshop ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-3`}>
        {isCarrierOrWorkshop && (
          <Link
            to="/jobs"
            className="relative w-full text-left bg-gradient-to-br from-blue-600 to-indigo-700 shadow-lg shadow-blue-200 text-white rounded-2xl p-4 h-full flex flex-col gap-1 transition-all hover:scale-[1.01] active:scale-[0.98] no-underline"
          >
            <div className="flex items-center justify-between">
              <div className="bg-white/20 rounded-xl p-2"><Star className="w-5 h-5" /></div>
              <ArrowUpRight className="w-4 h-4 opacity-40" />
            </div>
            <div className="text-xl font-bold mt-2 leading-tight">{availableJobs?.totalCount ?? '—'}</div>
            <div className="text-sm opacity-80">Available Jobs</div>
            <div className="text-xs opacity-55">
              {availableJobs?.totalCount === 0
                ? 'No active jobs in your area'
                : availableJobs?.totalCount === 1
                ? '1 active job matching your area'
                : `${availableJobs?.totalCount ?? 0} active jobs matching your area`}
            </div>
          </Link>
        )}
        <StatCard
          icon={<Briefcase className="w-5 h-5" />}
          label="Active Jobs"
          value={summary?.activeJobs ?? 0}
          sub={`${summary?.completedJobs ?? 0} completed`}
          gradient="bg-gradient-to-br from-emerald-500 to-green-700 shadow-lg shadow-green-200"
          selected={selectedCard === 'jobs'}
          onClick={() => toggleCard('jobs')}
        />
        <StatCard
          icon={<CreditCard className="w-5 h-5" />}
          label="Payments"
          value={`£${(summary?.availableBalance ?? 0).toFixed(2)}`}
          sub="available balance"
          gradient="bg-gradient-to-br from-purple-500 to-violet-700 shadow-lg shadow-purple-200"
          selected={selectedCard === 'payments'}
          onClick={() => toggleCard('payments')}
        />
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Referrals"
          value={referrals?.activeReferrals ?? 0}
          sub={`£${(referrals?.totalEarned ?? 0).toFixed(2)} earned`}
          gradient="bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg shadow-orange-200"
          selected={selectedCard === 'referrals'}
          onClick={() => toggleCard('referrals')}
        />
        <StatCard
          icon={<Shield className="w-5 h-5" />}
          label="Documents"
          value={approvedDocs}
          sub={docSub}
          gradient={`bg-gradient-to-br shadow-lg ${
            expiredCount > 0 ? 'from-red-500 to-red-700 shadow-red-200'
            : expiringSoon > 0 ? 'from-gray-400 to-gray-600 shadow-gray-200'
            : 'from-gray-400 to-gray-600 shadow-gray-200'
          }`}
          selected={selectedCard === 'documents'}
          onClick={() => toggleCard('documents')}
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Disputes"
          value={disputeSummary?.openCount ?? 0}
          sub={disputeSummary && disputeSummary.overdueSlaCount > 0 ? `${disputeSummary.overdueSlaCount} SLA breached` : 'open'}
          gradient="bg-gradient-to-br from-rose-300 to-pink-300 shadow-lg shadow-rose-100"
          selected={selectedCard === 'disputes'}
          onClick={() => toggleCard('disputes')}
        />
      </div>

      {/* Detail panels */}
      {selectedCard === 'jobs'      && <JobsPanel      jobs={myJobs}    onClose={() => setSelectedCard(null)} />}
      {selectedCard === 'payments'  && summary && <PaymentsPanel  summary={summary}  onClose={() => setSelectedCard(null)} />}
      {selectedCard === 'referrals' && referrals && <ReferralsPanel referrals={referrals} onClose={() => setSelectedCard(null)} />}
      {selectedCard === 'documents' && <DocumentsPanel documents={documents} onClose={() => setSelectedCard(null)} />}
      {selectedCard === 'disputes'  && <DisputesPanel disputes={myDisputes} onClose={() => setSelectedCard(null)} />}

      {/* Quick Links */}
      <QuickLinksGrid isCarrier={isCarrier} isWorkshop={isWorkshop} />

      {/* Local Info — Traffic News & Weather. Traffic News always gets its own full-width row
          (rather than a narrow sidebar column) so the incident map has enough room to be legible.
          Lazy-loaded via useInViewOnce: the header/skeleton render immediately (so the feature is
          still discoverable on first login) but the actual data/map only fetch once this section
          is scrolled into view — see useDashboardLocation's `enabled` param above. */}
      <div ref={localInfoRef} className="space-y-5" data-testid="dashboard-local-info-section">
        <h2 className="text-sm font-bold text-gray-700">Local Info</h2>
        {localInfoInView ? (
          <>
            <TrafficNewsSection location={dashboardLocation} incidents={trafficIncidents} isLoading={trafficLoading} error={trafficError} />
            <WeatherSection location={dashboardLocation} hours={hourlyWeather} isLoading={weatherLoading} error={weatherError} />
          </>
        ) : (
          <LocalInfoSkeleton />
        )}
      </div>

    </div>
  );
}
